import React from 'react';
import omit from 'lodash/omit';
import EditableDataTable from './EditableDataTable';
import styles from './EditableDataTable.module.css';
import TableBindingField from './TableBindingField';
import TableColumnsField from './TableColumnsField';
import TableRowsField from './TableRowsField';
import TableStructureField from './TableStructureField';
import TableStylePicker from './TableStylePicker';
import TableStyleSettings from './TableStyleSettings';
import { DEFAULT_TABLE_DESIGN } from './tableStyles';
import { puckAdvancedSectionField, puckSectionField, puckStyleGallerySectionField } from '../../../puck/fields/puckSectionField';
import { themedBlockProps, themedBlockResolveData } from '../../../puck/themedBlocks';
import { resolveVariableBoxFields, variableBoxFormFields } from '../../../puck/fields/variableBoxFormFields';
import { WEB_TABLE_BLOCK_TYPE } from '../../../utils/webTableRefresh';
import HiddenBlockFrame from '../../../puck/HiddenBlockFrame';

const BOUND_TABLE_HIDDEN_FIELDS = ['columns', 'rows', 'mergedCells'];

const DEFAULT_EDITABLE_TABLE_COLUMNS = [
  { key: 'col_1', label: 'Colonne 1', widthPercent: 50, cellType: 'text' },
  { key: 'col_2', label: 'Colonne 2', widthPercent: 50, cellType: 'text' },
];

const DEFAULT_EDITABLE_TABLE_ROWS = [
  { cells: [{ value: '' }, { value: '' }] },
  { cells: [{ value: '' }, { value: '' }] },
];


const editableDataTableConfig = {
  label: 'Tableau',
  defaultProps: {
    tableName: '',
    // Configuration d'un tableau branché : axes donnée (mode, variante…) et
    // overrides de présentation par colonne (masquage, ordre, largeur,
    // renommage). Même forme que `box.tableConfig` du Proposal — le moteur est
    // partagé (`src/shared/tables/`). `null` tant que rien n'est branché.
    tableConfig: null,
    // Document qui sert la table quand ce n'est pas celui qu'on édite : une
    // offre propose aussi les tables de ses opportunités rattachées, et
    // celles-ci ne se lisent que sur leur opportunité. `null` = le document
    // ouvert.
    tableSource: null,
    // Tableau découpé par entité (zone, profil…) : `content.shouldSplit` du
    // Proposal. Ses slices — une par entité, chacune avec ses colonnes et ses
    // lignes — vivent dans `splitSlices`, et le bloc les empile.
    shouldSplit: false,
    splitSlices: [],
    columns: DEFAULT_EDITABLE_TABLE_COLUMNS,
    rows: DEFAULT_EDITABLE_TABLE_ROWS,
    // Fusions de cellules et groupes de colonnes, même modèle que le Proposal
    // (clés de lignes et de colonnes, recalculées au rendu).
    mergedCells: [],
    columnsDisposition: [],
    design: DEFAULT_TABLE_DESIGN,
    draftStyleContext: { colors: [], fontFamilyList: [] },
    // Style du thème du site que le tableau suit (`design` en garde la copie).
    // Absent des défauts : ils se fusionnent aussi sous les blocs enregistrés.
    // Box variable (table_variable du classique) : `{ showInForm, name,
    // remark }`. `showInForm` n'a pas de défaut : absent, c'est le lien au
    // CRM qui décide (shared/variables).
    form: {},
  },
  // Titres de colonnes, texte des cellules et pied s'éditent UNIQUEMENT sur le
  // canvas. La sidebar ne porte que ce qu'aucune frappe ne saurait poser.
  fields: {
    _section1: puckSectionField('Données'),
    tableName: {
      type: 'custom',
      label: 'Variable tableau',
      render: (props) => <TableBindingField {...props} />,
    },
    columns: {
      type: 'custom',
      label: 'Colonnes',
      render: (props) => <TableColumnsField {...props} />,
    },
    rows: {
      type: 'custom',
      label: 'Lignes',
      render: (props) => <TableRowsField {...props} />,
    },
    mergedCells: {
      type: 'custom',
      label: 'Structure',
      render: () => <TableStructureField />,
    },

    _section2: puckStyleGallerySectionField('Tableau'),
    design: {
      type: 'object',
      label: 'Style du tableau',
      objectFields: {
        // Un seul champ : la galerie de styles ; les réglages fins vivent dans
        // « Réglages avancés » (TableStyleSettings). Déclaré sur
        // `useCustomBorder` faute de prop « style » — c'est la seule prop de
        // style sans réglage à elle, et la galerie écrit tout `design` par
        // dispatch de toute façon.
        useCustomBorder: {
          type: 'custom',
          label: 'Style du tableau',
          render: () => <TableStylePicker />,
        },
      },
    },

    _section3: puckAdvancedSectionField(),
    /* Pas de prop à lui : il écrit `design` par dispatch, comme la galerie.
       Puck n'accepte qu'un champ par prop, et `design` a déjà le sien. */
    _designSettings: {
      type: 'custom',
      label: 'Réglages du tableau',
      render: () => <TableStyleSettings />,
    },

    ...variableBoxFormFields({ withFormSection: true }),
  },
  // Déposé : style par défaut du thème. Réglé à la main : détaché du thème.
  resolveData: themedBlockResolveData('table'),
  // Tableau branché : colonnes, lignes et structure viennent de la table. Le
  // Proposal ne propose rien de tel pour un tableau variable — ce qui serait
  // saisi là disparaîtrait au prochain rafraîchissement.
  resolveFields: (data, { fields }) => {
    const resolved = resolveVariableBoxFields({ type: WEB_TABLE_BLOCK_TYPE, props: data.props }, fields);
    return data.props?.tableName ? omit(resolved, BOUND_TABLE_HIDDEN_FIELDS) : resolved;
  },
  render: (props) => {
    const {
      id,
      tableName,
      tableConfig,
      shouldSplit,
      splitSlices,
      columns,
      rows,
      mergedCells,
      columnsDisposition,
      design,
      draftStyleContext,
      footerData,
      drawMode,
      puck,
    } = themedBlockProps('table', props, props.puck?.metadata);
    const common = {
      id,
      tableName,
      tableConfig,
      shouldSplit,
      design,
      draftStyleContext: draftStyleContext || {},
      footerData,
      drawMode,
      puck,
    };
    const frame = (content) => (
      <HiddenBlockFrame isVisible={props.isVisible} isEditing={!!puck?.isEditing}>{content}</HiddenBlockFrame>
    );
    // Une slice par entité, l'une sous l'autre : chacune s'édite au canvas
    // dans sa propre slice (`contentPathPrefix`).
    if (shouldSplit && splitSlices?.length) {
      return frame(
        <div className={styles.splitStack}>
          {splitSlices.map((slice, index) => (
            <EditableDataTable
              key={index}
              {...common}
              contentPathPrefix={`splitSlices[${index}].`}
              columns={slice.columns}
              rows={slice.rows}
              columnsDisposition={slice.columnsDisposition}
            />
          ))}
        </div>,
      );
    }
    return frame(
      <EditableDataTable
        {...common}
        columns={columns}
        rows={rows}
        mergedCells={mergedCells}
        columnsDisposition={columnsDisposition}
      />,
    );
  },
};

export default editableDataTableConfig;
